import React, { Component } from "react";
import {
  dismissExperiencePermanently,
  emitExperienceHook,
  markExperienceSeen,
  normalizePresentation,
} from "./experienceHelpers";
import {
  beginCinematicPlayInit,
  cleanupCinematicPlayback,
  createCinematicPlaybackSession,
  snapshotCinematicMedia,
  unmuteCinematicPlayback,
} from "./experienceCinematicPlayback";
import "./ExperienceOverlay.css";

/**
 * Renders Experience Engine presentations (splash / cinematic).
 * On complete / skip / error → onComplete() so navigation is never blocked.
 */
class ExperienceOverlay extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showSkip: false,
      fadingOut: false,
      ready: false,
      videoMuted: false,
      showUnmute: false,
    };
    this.timers = [];
    this.completed = false;
    this.videoEl = null;
    this.audioEl = null;
    this.playbackSession = createCinematicPlaybackSession();

    const presentation = normalizePresentation(props.presentation);
    const mediaSnap = snapshotCinematicMedia(
      this.playbackSession,
      presentation.media
    );
    this.videoSrc = mediaSnap.src;
    this.videoPoster = mediaSnap.poster;
    this.audioSrc = mediaSnap.audioSrc;
    this.autoPlayEnabled =
      !presentation.behavior || presentation.behavior.auto_play !== false;
  }

  componentDidMount() {
    const presentation = normalizePresentation(this.props.presentation);
    if (!presentation.should_present) {
      this.finish("bypass");
      return;
    }

    emitExperienceHook(presentation, "started");

    const timing = presentation.timing || {};
    const fadeIn = timing.fade_in_ms != null ? timing.fade_in_ms : 400;
    const skipAfter = timing.skip_after_ms != null ? timing.skip_after_ms : 1500;
    const duration = timing.duration_ms != null ? timing.duration_ms : 2500;

    this.timers.push(
      setTimeout(() => this.setState({ ready: true }), 30)
    );

    if (presentation.behavior && presentation.behavior.allow_skip !== false) {
      this.timers.push(
        setTimeout(() => this.setState({ showSkip: true }), skipAfter)
      );
    }

    if (presentation.type === "splash") {
      this.timers.push(
        setTimeout(() => this.finish("completed"), fadeIn + duration)
      );
    }
  }

  componentWillUnmount() {
    this.clearTimers();
    cleanupCinematicPlayback(this.playbackSession, this.videoEl);
    if (this.audioEl && typeof this.audioEl.pause === "function") {
      try {
        this.audioEl.pause();
      } catch (e) {}
    }
    this.videoEl = null;
    this.audioEl = null;
  }

  clearTimers() {
    this.timers.forEach((t) => clearTimeout(t));
    this.timers = [];
  }

  finish = (reason) => {
    if (this.completed) {
      return;
    }
    this.completed = true;
    this.clearTimers();

    const presentation = normalizePresentation(this.props.presentation);
    const fadeOut =
      presentation.timing && presentation.timing.fade_out_ms != null
        ? presentation.timing.fade_out_ms
        : 400;

    if (reason === "skipped") {
      emitExperienceHook(presentation, "skipped");
    } else if (reason === "completed") {
      emitExperienceHook(presentation, "completed");
    }

    if (this.props.destinationType) {
      markExperienceSeen(this.props.destinationType, presentation);
    }

    this.setState({ fadingOut: true });
    this.timers.push(
      setTimeout(() => {
        if (typeof this.props.onComplete === "function") {
          this.props.onComplete(reason);
        }
      }, Math.min(fadeOut, 800))
    );
  };

  handleSkip = () => {
    this.finish("skipped");
  };

  handleDismissForever = () => {
    const presentation = normalizePresentation(this.props.presentation);
    dismissExperiencePermanently(this.props.destinationType, presentation);
    this.finish("dismissed");
  };

  handleVideoEnded = () => {
    this.finish("completed");
  };

  handleMediaError = () => {
    this.finish("error");
  };

  setVideoRef = (el) => {
    if (!el) {
      // Detach only. Do not reset playInitStarted — a host remount/retry
      // on this presentation instance must not start a second play() init.
      this.videoEl = null;
      return;
    }
    this.videoEl = el;
    if (this.autoPlayEnabled) {
      this.attemptVideoAutoplay(el);
    }
  };

  setAudioRef = (el) => {
    this.audioEl = el;
  };

  syncSeparateAudio = (muted) => {
    const audio = this.audioEl;
    if (!audio) {
      return;
    }
    audio.muted = muted;
    if (!muted && audio.paused) {
      const audioPlay = audio.play();
      if (audioPlay && typeof audioPlay.catch === "function") {
        audioPlay.catch(function () {});
      }
    }
  };

  attemptVideoAutoplay = (video) => {
    const self = this;
    beginCinematicPlayInit(this.playbackSession, video, {
      onUnmuted: function () {
        if (self.completed || self.playbackSession.cancelled) {
          return;
        }
        self.setState({ videoMuted: false, showUnmute: false });
        self.syncSeparateAudio(false);
      },
      onMutedFallback: function () {
        if (self.completed || self.playbackSession.cancelled) {
          return;
        }
        self.setState({ videoMuted: true, showUnmute: true });
        self.syncSeparateAudio(true);
      },
    });
  };

  handleUnmute = () => {
    unmuteCinematicPlayback(this.videoEl);
    this.setState({ videoMuted: false, showUnmute: false });
    this.syncSeparateAudio(false);
  };

  renderSplash(presentation) {
    const media = presentation.media || {};
    const style = {
      backgroundColor: media.background_color || "#0a0a0a",
    };

    return (
      <div className="ee-stage ee-splash" style={style}>
        {media.logo ? (
          <img className="ee-logo" src={media.logo} alt="" />
        ) : null}
        {media.image ? (
          <img
            className={
              "ee-media" + (this.state.ready ? " ee-media-visible" : "")
            }
            src={media.image}
            alt=""
            onError={this.handleMediaError}
          />
        ) : (
          <div
            className={
              "ee-media ee-splash-fallback" +
              (this.state.ready ? " ee-media-visible" : "")
            }
          />
        )}
      </div>
    );
  }

  renderCinematic(presentation) {
    const media = presentation.media || {};

    return (
      <div
        className="ee-stage ee-cinematic"
        style={{ backgroundColor: media.background_color || "#000" }}
      >
        {media.logo ? (
          <img className="ee-logo" src={media.logo} alt="" />
        ) : null}
        <video
          ref={this.setVideoRef}
          className={
            "ee-media" + (this.state.ready ? " ee-media-visible" : "")
          }
          src={this.videoSrc}
          poster={this.videoPoster ? this.videoPoster : undefined}
          autoPlay={false}
          muted={this.state.videoMuted}
          playsInline
          preload="auto"
          onEnded={this.handleVideoEnded}
          onError={this.handleMediaError}
        />
        {this.audioSrc ? (
          <audio
            ref={this.setAudioRef}
            src={this.audioSrc}
            autoPlay={false}
            muted={this.state.videoMuted}
          />
        ) : null}
        {this.state.showUnmute ? (
          <button
            type="button"
            className="ee-unmute"
            onClick={this.handleUnmute}
            aria-label="Unmute video"
          >
            Tap to unmute
          </button>
        ) : null}
      </div>
    );
  }

  render() {
    const presentation = normalizePresentation(this.props.presentation);
    if (!presentation.should_present) {
      return null;
    }

    const fadeMs =
      presentation.timing && presentation.timing.fade_in_ms != null
        ? presentation.timing.fade_in_ms
        : 400;

    return (
      <div
        className={
          "ee-overlay" + (this.state.fadingOut ? " ee-overlay-out" : "")
        }
        style={{
          "--ee-fade-ms": fadeMs + "ms",
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Experience"
      >
        {presentation.type === "cinematic"
          ? this.renderCinematic(presentation)
          : this.renderSplash(presentation)}

        {this.state.showSkip ? (
          <div className="ee-actions">
            <button
              type="button"
              className="ee-skip"
              onClick={this.handleSkip}
            >
              Skip
            </button>
            {presentation.behavior &&
            presentation.behavior.allow_permanent_dismissal ? (
              <button
                type="button"
                className="ee-dismiss"
                onClick={this.handleDismissForever}
              >
                Don&apos;t show again
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }
}

export default ExperienceOverlay;
